<template>
  <div class="scav-registration-page">
    <div class="registration-container">
      <div class="registration-header">
        <img v-if="orgBranding.logo_url" :src="orgBranding.logo_url" alt="SCAV" class="scav-logo" />
        <h1>{{ orgBranding.app_name || 'SCAV Member Portal' }}</h1>
        <p class="subtitle">Enter your SCAV membership email to get started</p>
      </div>

      <!-- Email Entry Form -->
      <div v-if="!emailSent" class="email-form-section">
        <form @submit.prevent="checkMembership" class="member-email-form">
          <div class="form-group">
            <label for="email">SCAV Membership Email</label>
            <input 
              id="email"
              v-model="email" 
              type="email" 
              class="form-control"
              placeholder="your.email@example.com"
              required
              :disabled="loading"
            />
            <small class="form-text">Use the email address associated with your SCAV membership</small>
          </div>

          <button 
            type="submit" 
            class="btn btn-primary btn-block" 
            :disabled="loading"
          >
            <span v-if="loading">
              <i class="fas fa-spinner fa-spin"></i> Checking membership...
            </span>
            <span v-else>
              <i class="fas fa-user-check"></i> Continue
            </span>
          </button>

          <div v-if="error" class="alert alert-danger mt-3">
            <i class="fas fa-exclamation-circle"></i>
            {{ error }}
            <a v-if="helpUrl" :href="helpUrl" target="_blank" class="alert-link ml-2">
              Learn more <i class="fas fa-external-link-alt"></i>
            </a>
          </div>
        </form>

        <div class="help-section mt-4">
          <p class="text-center text-muted">
            <strong>Not a member?</strong> 
            <a href="https://scav.org/join" target="_blank">Join SCAV <i class="fas fa-external-link-alt"></i></a>
          </p>
          <p class="text-center text-muted">
            <strong>Membership lapsed?</strong> 
            <a href="https://scav.org/renew" target="_blank">Renew now <i class="fas fa-external-link-alt"></i></a>
          </p>
          <p class="text-center text-muted mt-3">
            <router-link to="/login">Already have an account? Log in</router-link>
          </p>
        </div>
      </div>

      <!-- Email Sent Confirmation -->
      <div v-else class="email-sent-section">
        <div class="success-icon-wrapper">
          <i class="fas fa-envelope-circle-check success-icon"></i>
        </div>
        
        <h2>Check Your Email!</h2>
        <p class="email-sent-message">We sent a verification link to:</p>
        <p class="email-highlight">{{ email }}</p>
        
        <div class="member-info-card">
          <h3><i class="fas fa-id-card"></i> Member Details Found</h3>
          <ul class="member-details-list">
            <li>
              <strong>Name:</strong> 
              <span>{{ memberName }}</span>
            </li>
            <li>
              <strong>Status:</strong> 
              <span class="badge badge-success">
                <i class="fas fa-check-circle"></i> {{ memberStatus }}
              </span>
            </li>
          </ul>
        </div>

        <div class="instructions-box">
          <i class="fas fa-info-circle"></i>
          <p>
            Click the link in your email to complete your account setup.
            The link will expire in <strong>24 hours</strong>.
          </p>
        </div>

        <button @click="resetForm" class="btn btn-link mt-3">
          <i class="fas fa-arrow-left"></i> Use a different email
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'RegisterSCAV',
  data() {
    return {
      email: '',
      loading: false,
      error: null,
      helpUrl: null,
      emailSent: false,
      memberName: '',
      memberStatus: '',
      orgBranding: {}
    }
  },
  mounted() {
    // Load SCAV branding
    this.loadSCAVBranding()
  },
  methods: {
    async loadSCAVBranding() {
      try {
        const response = await api.get('/users/organizations/colibri-media-group/')
        if (response.data.branding_settings) {
          this.orgBranding = response.data.branding_settings
        }
      } catch (error) {
        console.warn('Could not load SCAV branding:', error)
      }
    },
    
    async checkMembership() {
      this.loading = true
      this.error = null
      this.helpUrl = null

      try {
        const response = await api.post('/users/register-scav-member/', {
          email: this.email
        })

        if (response.data.success) {
          this.emailSent = true
          this.memberName = response.data.member_name
          this.memberStatus = response.data.member_status
        }
      } catch (error) {
        if (error.response?.data) {
          this.error = error.response.data.error || error.response.data.message
          this.helpUrl = error.response.data.help_url
        } else {
          this.error = 'Unable to verify membership. Please try again.'
        }
      } finally {
        this.loading = false
      }
    },
    
    resetForm() {
      this.email = ''
      this.emailSent = false
      this.error = null
      this.helpUrl = null
      this.memberName = ''
      this.memberStatus = ''
    }
  }
}
</script>

<style scoped>
.scav-registration-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
}

.registration-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  padding: 40px;
  max-width: 500px;
  width: 100%;
}

.registration-header {
  text-align: center;
  margin-bottom: 30px;
}

.scav-logo {
  max-width: 200px;
  height: auto;
  margin-bottom: 20px;
}

.registration-header h1 {
  font-size: 24px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 8px;
}

.subtitle {
  color: #718096;
  font-size: 14px;
}

.email-form-section {
  margin-top: 20px;
}

.form-group label {
  font-weight: 500;
  color: #2d3748;
  margin-bottom: 8px;
  display: block;
}

.form-control {
  width: 100%;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s ease;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-control:disabled {
  background: #f7fafc;
  cursor: not-allowed;
}

.form-text {
  display: block;
  margin-top: 6px;
  color: #a0aec0;
  font-size: 13px;
}

.btn {
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5568d3;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-block {
  width: 100%;
  margin-top: 16px;
}

.btn-link {
  background: none;
  color: #667eea;
  padding: 8px 16px;
}

.btn-link:hover {
  text-decoration: underline;
}

.alert {
  padding: 12px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.alert-danger {
  background: #fed7d7;
  color: #c53030;
  border: 1px solid #fc8181;
}

.alert-link {
  color: #9b2c2c;
  text-decoration: underline;
}

.help-section {
  border-top: 1px solid #e2e8f0;
  padding-top: 20px;
}

.help-section p {
  margin: 8px 0;
}

/* Email Sent Section */
.email-sent-section {
  text-align: center;
}

.success-icon-wrapper {
  margin-bottom: 24px;
}

.success-icon {
  font-size: 64px;
  color: #48bb78;
}

.email-sent-section h2 {
  font-size: 24px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 12px;
}

.email-sent-message {
  color: #718096;
  margin-bottom: 8px;
}

.email-highlight {
  font-size: 18px;
  font-weight: 600;
  color: #667eea;
  margin-bottom: 24px;
}

.member-info-card {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  margin: 24px 0;
  text-align: left;
}

.member-info-card h3 {
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.member-details-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.member-details-list li {
  padding: 8px 0;
  border-bottom: 1px solid #e2e8f0;
}

.member-details-list li:last-child {
  border-bottom: none;
}

.member-details-list strong {
  color: #4a5568;
  margin-right: 8px;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
}

.badge-success {
  background: #c6f6d5;
  color: #22543d;
}

.instructions-box {
  background: #edf2f7;
  border-left: 4px solid #667eea;
  padding: 16px;
  margin: 24px 0;
  text-align: left;
  display: flex;
  gap: 12px;
}

.instructions-box i {
  color: #667eea;
  margin-top: 2px;
}

.instructions-box p {
  margin: 0;
  color: #4a5568;
  font-size: 14px;
  line-height: 1.5;
}

@media (max-width: 576px) {
  .registration-container {
    padding: 24px;
  }
  
  .registration-header h1 {
    font-size: 20px;
  }
  
  .scav-logo {
    max-width: 150px;
  }
}
</style>

